import { Link } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-canvas text-fg">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="brand" aria-label="Berg Familia Budget home">
            <BrandMark className="h-7 w-auto" />
          </Link>
          <Link to="/" className="text-xs text-fg-secondary hover:text-fg">
            Back to app
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Privacy Policy</h1>
          <p className="text-sm text-fg-tertiary">
            Effective date: 2026-05-10 &middot; Last updated: 2026-05-10
          </p>
        </div>

        <Section title="1. About this application">
          <p>
            Berg Familia Budget (the &ldquo;application&rdquo;) is a private household budgeting tool
            operated by Dakota Berg for the use of two named household members. It is not offered
            to anyone outside the household, is not commercial, and has no other users.
          </p>
          <p>
            This privacy policy describes the categories of personal information the application
            collects, how that information is used, who it is shared with, how it is protected,
            and the rights of the individuals whose information is collected.
          </p>
        </Section>

        <Section title="2. Who we are">
          <p>
            <strong>Operator:</strong> Dakota Berg
            <br />
            <strong>Contact:</strong>{' '}
            <a href="mailto:dakotaberg@gmail.com" className="text-accent hover:underline">
              dakotaberg@gmail.com
            </a>
            <br />
            <strong>Application:</strong>{' '}
            <a href="https://budget.bergfamilia.com" className="text-accent hover:underline">
              budget.bergfamilia.com
            </a>
          </p>
        </Section>

        <Section title="3. Information we collect">
          <p>The application collects the following categories of information:</p>
          <List>
            <li>
              <strong>Identity information from Google.</strong> When a household member signs in
              with Google, the application receives their email address, display name, and profile
              picture URL from Google&rsquo;s OAuth response. The Google subject identifier
              (a stable unique ID) is also stored to recognize repeat sign-ins.
            </li>
            <li>
              <strong>Financial account and transaction information from Plaid.</strong> When a
              household member connects a bank or credit card account using Plaid, the application
              receives, on an ongoing basis: account name, account type, last four digits of the
              account number, current and available balances, and the transactions for that
              account. Up to 24 months of historical transactions are retrieved on initial
              connection, and new transactions are retrieved as Plaid notifies the application of
              changes.
            </li>
            <li>
              <strong>User-generated budget data.</strong> Categories, budgets, bills, and
              category-rule overrides created by household members inside the application.
            </li>
            <li>
              <strong>Operational logs.</strong> Standard request and error logs retained by the
              application&rsquo;s hosting provider, database provider, and identity provider for
              security and debugging purposes.
            </li>
          </List>
          <p>
            The application does not use cookies for tracking. It uses browser local storage to
            hold a session token after sign-in. It does not embed any third-party analytics,
            advertising, or marketing tags.
          </p>
        </Section>

        <Section title="4. How we use your information">
          <p>The information described in §3 is used only to:</p>
          <List>
            <li>Authenticate household members and authorize access to the application.</li>
            <li>
              Display each member&rsquo;s own financial transactions and balances, plus
              transactions from joint accounts shared with the other household member.
            </li>
            <li>
              Compute household-level budget aggregates (totals by category) for the budget view.
            </li>
            <li>Apply user-created category rules to past and future transactions.</li>
            <li>Operate, secure, and debug the application.</li>
          </List>
          <p>
            Personal financial information is <strong>not</strong> used for advertising, sold,
            shared with third parties for marketing, or used to train any machine-learning model.
          </p>
        </Section>

        <Section title="5. Privacy between household members">
          <p>
            Transactions in personal accounts are visible only to the household member who owns the
            account. Transactions in accounts that are auto-classified or manually marked as joint
            are visible to both household members. The Budget view shows category totals
            aggregated across the household.
          </p>
          <p>
            This boundary is enforced server-side: the application does not trust the browser to
            filter out another member&rsquo;s private data.
          </p>
        </Section>

        <Section title="6. Service providers">
          <p>
            The application relies on the following service providers (sub-processors), each of
            which receives only the data necessary to perform its function:
          </p>
          <List>
            <li>
              <strong>Plaid Inc.</strong> &mdash; financial account and transaction data provider.
              Plaid is the source of bank/card data displayed in the application. Plaid&rsquo;s
              own privacy practices are described at{' '}
              <a
                href="https://plaid.com/legal/#end-user-privacy-policy"
                className="text-accent hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                plaid.com/legal
              </a>
              .
            </li>
            <li>
              <strong>Google LLC</strong> &mdash; identity provider for sign-in.
            </li>
            <li>
              <strong>Vercel Inc.</strong> &mdash; application hosting (serverless functions and
              static asset delivery) and DNS.
            </li>
            <li>
              <strong>Neon, Inc.</strong> &mdash; managed Postgres database hosting.
            </li>
            <li>
              <strong>GitHub, Inc.</strong> &mdash; source code hosting (does not receive
              production data).
            </li>
          </List>
          <p>
            No other third parties receive personal information from the application.
          </p>
        </Section>

        <Section title="7. How we protect your information">
          <List>
            <li>
              All traffic to and from the application is encrypted in transit (HTTPS/TLS 1.2 or
              higher).
            </li>
            <li>
              Plaid access tokens are encrypted at rest using AES-256-GCM before being written to
              the database.
            </li>
            <li>
              The database (Neon Postgres) encrypts all data at rest and is reachable only over
              TLS using credentialed connections.
            </li>
            <li>
              Sign-in is restricted to an explicit email allowlist. Administrative consoles
              (hosting, database, source, identity, financial integration) are protected with
              multi-factor authentication using a hardware-backed authenticator app.
            </li>
            <li>
              Application secrets and signing keys are stored only in the hosting provider&rsquo;s
              encrypted environment-variable store and are never committed to source.
            </li>
          </List>
          <p>
            Additional detail on the technical and organizational controls applied to the
            application is documented in the{' '}
            <a
              href="https://github.com/dakotaberg/budget/blob/main/legal/information-security-policy.md"
              className="text-accent hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Information Security Policy
            </a>{' '}
            and the{' '}
            <a
              href="https://github.com/dakotaberg/budget/blob/main/legal/access-control-policy.md"
              className="text-accent hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Access Control Policy
            </a>
            .
          </p>
        </Section>

        <Section title="8. Data retention and disposal">
          <List>
            <li>
              <strong>Active connections.</strong> Account, balance, and transaction data is
              retained for as long as the corresponding bank connection is linked. Up to 24 months
              of historical transactions are retrieved at connection time.
            </li>
            <li>
              <strong>Disconnection.</strong> When a household member disconnects a bank
              connection in the application, the encrypted access token, accounts, transactions,
              and connection-scoped category rules are deleted from the database.
            </li>
            <li>
              <strong>Account removal.</strong> When a household member is removed from the
              allowlist, their session is invalidated and their personal data may be deleted from
              the database on request.
            </li>
            <li>
              <strong>Backups.</strong> The database provider (Neon) retains automated
              point-in-time backups per its standard retention. Deletions propagate through these
              backups within the provider&rsquo;s retention window.
            </li>
            <li>
              <strong>Decommissioning.</strong> If the application is shut down, the production
              database, hosting project, OAuth credentials, and Plaid integration are deleted.
            </li>
          </List>
        </Section>

        <Section title="9. Your rights">
          <p>As a household member you may, at any time:</p>
          <List>
            <li>
              <strong>Access</strong> your data &mdash; the application UI surfaces all personal
              data the application stores about you.
            </li>
            <li>
              <strong>Correct</strong> your data &mdash; via the relevant settings and rules pages
              in the application.
            </li>
            <li>
              <strong>Disconnect</strong> a bank connection &mdash; from the Settings &gt; Links
              page; this triggers the disposal described in §8.
            </li>
            <li>
              <strong>Request deletion</strong> of your account and associated data &mdash; by
              contacting the operator at the email address in §2.
            </li>
            <li>
              <strong>Withdraw consent</strong> for any processing &mdash; by contacting the
              operator. The application cannot continue to provide service to a household member
              who withdraws consent for the data the application is built on.
            </li>
          </List>
          <p>
            Plaid additionally provides controls for end users to manage and disconnect their
            connections through{' '}
            <a
              href="https://my.plaid.com"
              className="text-accent hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              my.plaid.com
            </a>
            .
          </p>
        </Section>

        <Section title="10. Children">
          <p>
            The application is not intended for, marketed to, or used by children under 13, and
            does not knowingly collect information from children.
          </p>
        </Section>

        <Section title="11. International transfers">
          <p>
            The application is operated from the United States. Data is processed and stored in
            the United States by the providers listed in §6. Household members are aware of and
            consent to this processing as part of using the application.
          </p>
        </Section>

        <Section title="12. Changes to this policy">
          <p>
            Material changes to this policy will be communicated to household members directly and
            will be reflected by an updated &ldquo;Last updated&rdquo; date at the top of this
            page. The full revision history is available in the application&rsquo;s{' '}
            <a
              href="https://github.com/dakotaberg/budget/commits/main/apps/web/src/pages/PrivacyPolicyPage.tsx"
              className="text-accent hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              source repository
            </a>
            .
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Questions about this policy or about how the application handles your data should be
            directed to{' '}
            <a href="mailto:dakotaberg@gmail.com" className="text-accent hover:underline">
              dakotaberg@gmail.com
            </a>
            .
          </p>
        </Section>
      </main>

      <footer className="border-t border-border mt-10">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-fg-tertiary">
          &copy; Berg Familia Budget &middot;{' '}
          <Link to="/" className="hover:text-fg">
            Return to app
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-fg-secondary">{children}</div>
    </section>
  );
}

function List({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 space-y-2">{children}</ul>;
}
